import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Box,
  Typography,
  Divider,
  Chip,
  Stack,
  CircularProgress,
  Checkbox,
  FormGroup,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import EditIcon from "@mui/icons-material/Edit";
import HtmlWithLatex from "../pages/QuestionBank/components/HtmlWithLatex";

const STATUS_CONFIG = {
  approved: { label: "Approved", color: "success", icon: <CheckCircleOutlineIcon fontSize="small" /> },
  rejected: { label: "Rejected", color: "error",   icon: <CancelOutlinedIcon fontSize="small" /> },
};

function PlatformVerdictChip({ platform, verdict }) {
  if (!verdict) {
    return <Chip label={`${platform}: —`} size="small" variant="outlined" sx={{ fontSize: "0.7rem" }} />;
  }
  const cfg = STATUS_CONFIG[verdict] ?? { label: verdict, color: "default" };
  return (
    <Chip
      label={`${platform}: ${cfg.label}`}
      size="small"
      color={cfg.color}
      variant="outlined"
      sx={{ fontSize: "0.7rem" }}
    />
  );
}

function CorrectAnswerDisplay({ question }) {
  if (!question) return null;

  const type = question.type || "";
  const isMcqLike = ["multiple-choice", "multiple-select", "true-false"].includes(type);
  const isBlankType = ["fill-in-the-blank", "multi-fill", "table"].includes(type);
  const isTextType = ["short-answer", "essay"].includes(type);

  const options = question.options || [];
  const correctOptionsByFlag = options.filter((o) => o.is_correct);
  const correctOptionIds = question.answer?.correct_options || question.answer_data?.correct_options || [];
  const correctOptions = isMcqLike
    ? (correctOptionsByFlag.length > 0
        ? correctOptionsByFlag
        : options.filter((_, i) => correctOptionIds.includes(i) || correctOptionIds.includes(String(i))))
    : [];

  const correctAnswer =
    question.answer?.correct_answer_text ||
    question.answer_data?.correct_answer ||
    question.answer?.correct_answer ||
    "";

  const correctBlanks = isBlankType
    ? (question.answer?.correct_blanks ||
       question.answer_data?.correct_blanks ||
       question.answer_data?.blank_labels ||
       [])
    : [];

  const explanation = question.explanation || question.answer_data?.explanation || "";

  const unknownType = !isMcqLike && !isBlankType && !isTextType;
  const fallbackOptions = unknownType ? options.filter((o) => o.is_correct) : [];
  const fallbackBlanks = unknownType ? (question.answer?.correct_blanks || question.answer_data?.correct_blanks || []) : [];

  const hasContent =
    correctOptions.length > 0 ||
    correctBlanks.length > 0 ||
    correctAnswer ||
    explanation ||
    fallbackOptions.length > 0 ||
    fallbackBlanks.length > 0;

  if (!hasContent) {
    return (
      <Typography variant="body2" color="text.disabled" sx={{ fontStyle: "italic" }}>
        No answer data available.
      </Typography>
    );
  }

  const showOptions = correctOptions.length > 0 || fallbackOptions.length > 0;
  const displayOptions = correctOptions.length > 0 ? correctOptions : fallbackOptions;
  const showBlanks = correctBlanks.length > 0 || fallbackBlanks.length > 0;
  const displayBlanks = correctBlanks.length > 0 ? correctBlanks : fallbackBlanks;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {showOptions && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
            Correct option(s):
          </Typography>
          {displayOptions.map((o, i) => (
            <Box key={o.id ?? i} sx={{ pl: 1 }}>
              {o.image_url
                ? <Box component="img" src={o.image_url} sx={{ maxHeight: 48, maxWidth: 100, borderRadius: 1, mt: 0.25 }} />
                : <HtmlWithLatex html={o.option_text || o.text || ""} />}
            </Box>
          ))}
        </Box>
      )}
      {showBlanks && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
            Blank answers:
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {displayBlanks.map((b, i) => (
              <Chip key={i} label={<HtmlWithLatex html={String(Array.isArray(b) ? b[0] : b)} />} size="small" color="success" variant="outlined" />
            ))}
          </Box>
        </Box>
      )}
      {(isTextType || (!showOptions && !showBlanks)) && correctAnswer && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
            Answer:
          </Typography>
          <HtmlWithLatex html={String(correctAnswer)} />
        </Box>
      )}
      {explanation && (
        <Box sx={{ mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
            Explanation:
          </Typography>
          <HtmlWithLatex html={explanation} />
        </Box>
      )}
    </Box>
  );
}

function QuestionImages({ question }) {
  if (!question) return null;
  const urls = Array.isArray(question.image_urls) && question.image_urls.filter(Boolean).length > 0
    ? question.image_urls.filter(Boolean)
    : question.image_url ? [question.image_url] : [];
  if (!urls.length) return null;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
      {urls.map((src, i) => (
        <Box
          key={i}
          component="img"
          src={src}
          alt=""
          onError={(e) => { e.target.style.display = "none"; }}
          sx={{ maxWidth: "100%", maxHeight: 260, objectFit: "contain", borderRadius: 1, border: "1px solid", borderColor: "divider" }}
        />
      ))}
    </Box>
  );
}

function SubQuestionAnswerSection({ subQuestions }) {
  if (!subQuestions?.length) return null;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {subQuestions.map((sq, idx) => (
        <Box
          key={sq.id ?? idx}
          sx={{ pl: 1.5, borderLeft: "3px solid", borderColor: "primary.100", py: 0.5 }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", display: "block", mb: 0.5 }}>
            Part {idx + 1}
            {sq.question_text ? ": " : ""}
          </Typography>
          {sq.question_text && (
            <Box sx={{ mb: 0.75 }}>
              <HtmlWithLatex html={sq.question_text} />
            </Box>
          )}
          <QuestionImages question={sq} />
          <CorrectAnswerDisplay question={sq} />
        </Box>
      ))}
    </Box>
  );
}

export default function QuestionVerifyDialog({
  open,
  question,
  onClose,
  onSubmit,
  onEdit,
  itemData,
  submitting,
  loading,
}) {
  const [status, setStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [platforms, setPlatforms] = useState({ web: true, mobile: false });
  const [statusHint, setStatusHint] = useState("");

  useEffect(() => {
    if (!open) return;

    const myVerdicts = itemData?.my_verdicts ?? {};
    const nextPlatforms = {
      web: true,
      mobile: false,
    };
    if (myVerdicts.web) nextPlatforms.web = true;
    if (myVerdicts.mobile) nextPlatforms.mobile = true;

    setPlatforms(nextPlatforms);
    setNotes("");

    const selected = ["web", "mobile"].filter((p) => nextPlatforms[p]);
    const priorStatuses = selected.map((p) => myVerdicts[p]).filter(Boolean);
    const uniqueStatuses = [...new Set(priorStatuses)];

    if (uniqueStatuses.length === 1 && (uniqueStatuses[0] === "approved" || uniqueStatuses[0] === "rejected")) {
      setStatus(uniqueStatuses[0]);
      setStatusHint("");
    } else if (uniqueStatuses.length > 1) {
      setStatus("");
      setStatusHint("Platforms have different prior verdicts — choose a new one");
    } else {
      setStatus("");
      setStatusHint("");
    }
  }, [open, itemData?.my_verdicts]);

  const aggregate = itemData?.aggregate ?? { approved: 0, rejected: 0, flagged: 0, total: 0 };
  const myVerdicts = itemData?.my_verdicts ?? {};
  const isGrouped = question?.type === "grouped" && Array.isArray(question?.sub_questions) && question.sub_questions.length > 0;
  const selectedPlatforms = ["web", "mobile"].filter((p) => platforms[p]);

  const handlePlatformToggle = (platform) => {
    setPlatforms((prev) => {
      const next = { ...prev, [platform]: !prev[platform] };
      const count = Object.values(next).filter(Boolean).length;
      if (count === 0) return prev;
      return next;
    });
  };

  const handleSubmit = () => {
    onSubmit({ status, notes, platforms: selectedPlatforms });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pb: 1 }}>
        Verify Question
        {question?.paper_number && (
          <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 1 }}>
            #{question.paper_number}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {(question?.question_text || question?.image_url || question?.image_urls?.length) && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
              Question
            </Typography>
            {question?.question_text && <HtmlWithLatex html={question.question_text} />}
            <QuestionImages question={question} />
          </Box>
        )}

        <Divider sx={{ my: 1.5 }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
            Correct Answer & Explanation
          </Typography>
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">Loading answer…</Typography>
            </Box>
          ) : isGrouped ? (
            <SubQuestionAnswerSection subQuestions={question.sub_questions} />
          ) : (
            <CorrectAnswerDisplay question={question} />
          )}
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {aggregate.total > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
              Other testers ({aggregate.total})
            </Typography>
            <Stack direction="row" spacing={1}>
              {aggregate.approved > 0 && (
                <Chip icon={<CheckCircleOutlineIcon />} label={`${aggregate.approved} Approved`} color="success" size="small" variant="outlined" />
              )}
              {aggregate.rejected > 0 && (
                <Chip icon={<CancelOutlinedIcon />} label={`${aggregate.rejected} Rejected`} color="error" size="small" variant="outlined" />
              )}
            </Stack>
          </Box>
        )}

        {(myVerdicts.web || myVerdicts.mobile) && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
              Your prior verdicts
            </Typography>
            <Stack direction="row" spacing={1}>
              <PlatformVerdictChip platform="Web" verdict={myVerdicts.web} />
              <PlatformVerdictChip platform="Mobile" verdict={myVerdicts.mobile} />
            </Stack>
          </Box>
        )}

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
            Your verdict
          </Typography>
          <RadioGroup row value={status} onChange={(e) => setStatus(e.target.value)}>
            {Object.entries(STATUS_CONFIG).map(([val, cfg]) => (
              <FormControlLabel
                key={val}
                value={val}
                control={<Radio size="small" color={cfg.color} />}
                label={cfg.label}
              />
            ))}
          </RadioGroup>
          {statusHint && (
            <Typography variant="caption" color="warning.main" sx={{ display: "block", mb: 0.5 }}>
              {statusHint}
            </Typography>
          )}
          <TextField
            label={status === "rejected" ? "Notes (required)" : "Notes (optional)"}
            multiline
            minRows={2}
            fullWidth
            size="small"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            error={status === "rejected" && !notes.trim()}
            helperText={status === "rejected" && !notes.trim() ? "Please explain why this question is being rejected" : ""}
            sx={{ mt: 1 }}
          />
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
              Platform
            </Typography>
            <FormGroup row>
              <FormControlLabel
                control={<Checkbox size="small" checked={platforms.web} onChange={() => handlePlatformToggle("web")} />}
                label="Web"
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={platforms.mobile} onChange={() => handlePlatformToggle("mobile")} />}
                label="Mobile"
              />
            </FormGroup>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onClose} disabled={submitting}>Cancel</Button>
          {onEdit && !loading && question && (
            <Button
              onClick={() => onEdit(question)}
              startIcon={<EditIcon fontSize="small" />}
              disabled={submitting}
              variant="outlined"
              color="secondary"
              sx={{ textTransform: "none" }}
            >
              Edit Question
            </Button>
          )}
        </Box>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color={STATUS_CONFIG[status]?.color ?? "primary"}
          disabled={submitting || !status || selectedPlatforms.length === 0 || (status === "rejected" && !notes.trim())}
          startIcon={submitting ? <CircularProgress size={16} /> : STATUS_CONFIG[status]?.icon}
        >
          {submitting ? "Saving…" : status ? `Submit (${STATUS_CONFIG[status]?.label})` : "Select a verdict"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
